/**
 * User-facing request creation + admin-side request inbox + access-request
 * approval + needs-attention feed. Backed by sys__user_event_log + dispatch
 * failure aggregation.
 */

import { get, post } from '../_shared.ts';

// ---------------------------------------------------------------------
// User requests (D-091 + D-096), backed by sys__user_event_log
// ---------------------------------------------------------------------

export type RequestType =
  | 'connector_access'
  | 'new_connector'
  | 'custom_connector'
  | 'feedback'
  | 'suggestion'
  | 'support';

export interface UserRequest {
  event_id: string;
  /** Push 4-1d smoke-fix (2026-07-02): sys__event_log.short_id
   *  (evt-prefix) — deep-link target for /activity/:short_id from the
   *  admin request inbox cards. Optional so /api/me/requests (which
   *  does not yet return it) keeps compiling until wired end-to-end. */
  event_short_id?: string;
  request_type: RequestType | string;
  template_name: string | null;
  template_display_name: string | null;
  subject: string | null;
  body: string | null;
  status: string;
  resolution_note: string | null;
  created_at: string;
}

export function fetchMyRequests(): Promise<{ requests: UserRequest[] }> {
  return get('/api/me/requests');
}

export function createRequest(payload: {
  request_type: RequestType;
  subject?: string;
  body?: string;
  template_name?: string;
  /** Push #92 — where this request routes for notification. 'olytic' →
   * always-fire to cameron@olytic.com (no admin fanout).
   * 'admin' → fan out to workspace admins via their notify_user_help_request
   * preference (no Olytic notification). Required so the server can tell
   * an admin-bound "Ask a question" apart from an Olytic-bound "Contact
   * Olytic Support" (both use request_type='support' historically). */
  destination?: 'admin' | 'olytic';
}): Promise<{ ok: true; event_id: string; request_type: string }> {
  return post('/api/me/requests', payload);
}

export interface AttentionRow {
  attention_type: 'oauth_reauth' | 'dispatch_failed';
  ref_id: string;
  account_user_id: string | null;
  user_display_name: string | null;
  connector_template_name: string;
  connector_display_name: string;
  detail: string;
  created_at: string;
}

export function fetchAdminNeedsAttention(): Promise<{ attention: AttentionRow[] }> {
  return get('/api/admin/needs-attention');
}

export function fetchAdminRequests(opts?: {
  status?: 'open' | 'resolved' | 'rejected' | 'all';
  type?: RequestType | 'all';
}): Promise<{
  requests: Array<
    UserRequest & {
      account_user_id: string;
      requester_display_name: string;
      resolved_by_display_name: string | null;
      resolved_at: string | null;
      admin_actionable: boolean;
    }
  >;
}> {
  const qs: string[] = [];
  if (opts?.status !== undefined) qs.push(`status=${encodeURIComponent(opts.status)}`);
  if (opts?.type !== undefined) qs.push(`type=${encodeURIComponent(opts.type)}`);
  const suffix = qs.length > 0 ? `?${qs.join('&')}` : '';
  return get(`/api/admin/requests${suffix}`);
}

export interface AccessRequest {
  event_id: string;
  account_user_id: string;
  requester_display_name: string;
  template_name: string;
  template_display_name: string;
  note: string | null;
  status: string;
  created_at: string;
}

export function fetchAccessRequests(): Promise<{ requests: AccessRequest[] }> {
  return get<{ requests: AccessRequest[] }>('/api/admin/requests');
}

export function resolveAccessRequest(
  eventId: string,
  resolution: 'granted' | 'denied',
  instanceId?: string,
): Promise<{ ok: true }> {
  return post('/api/admin/requests/resolve', {
    event_id: eventId,
    resolution,
    ...(instanceId !== undefined ? { connector_instance_id: instanceId } : {}),
  });
}
